import gsap from "gsap";

const GAP = 12;
const ENTRANCE_DELAY = 1;

class NotifStack {
  #stack = [];
  #order = new WeakMap();
  #nextOrder = 0;
  // Card heights differ between breakpoints (e.g. 444px wide on desktop vs.
  // full width on mobile), so the stack re-lays itself out whenever one of its
  // cards changes size instead of relying on heights read at register time.
  #resizeObserver = new ResizeObserver(() => this.#reposition());

  // `order` is assigned once per element and kept even across unregister() —
  // so an item that temporarily leaves the stack (e.g. ModalProductPush
  // switching to `position: absolute` once its hero scrolls past) returns to
  // its original slot instead of jumping back on top of everything else.
  register(el, { delay = 0, skipEntrance = false } = {}) {
    if (this.#stack.some((n) => n.el === el)) return;

    if (!this.#order.has(el)) {
      this.#order.set(el, this.#nextOrder++);
    }

    const width = el.offsetWidth;

    this.#stack.push({ el, order: this.#order.get(el) });
    this.#stack.sort((a, b) => a.order - b.order);
    this.#reposition(el);
    this.#resizeObserver.observe(el);

    // skipEntrance: for an item rejoining the stack under its own logic (e.g.
    // ModalProductPush coming back from `position: absolute`/sticky) rather
    // than appearing for the first time — snaps into place instead of
    // replaying the slide-in-from-the-right.
    if (skipEntrance) {
      gsap.set(el, { x: 0 });
      return;
    }

    gsap.fromTo(
      el,
      { x: width + GAP },
      { x: 0, duration: 0.6, ease: "power3.out", delay: ENTRANCE_DELAY + delay }
    );
  }

  dismiss(el) {
    const width = el.offsetWidth;
    gsap.to(el, {
      x: width + GAP,
      duration: 0.2,
      ease: "power3.inOut",
      onComplete: () => {
        el.style.display = "none";
        this.#resizeObserver.unobserve(el);
        this.#stack = this.#stack.filter((n) => n.el !== el);
        this.#reposition();
      },
    });
  }

  // Removes `el` from the stack without animating it away or hiding it — for
  // items that leave the fixed stack under their own positioning logic rather
  // than being dismissed by the user. The rest of the stack closes the gap;
  // register() brings it back into its original slot later.
  unregister(el) {
    this.#resizeObserver.unobserve(el);
    this.#stack = this.#stack.filter((n) => n.el !== el);
    this.#reposition();
  }

  // `instantEl`, when given, is set (not animated) to its new bottom — used
  // for the element that just registered, whose vertical position should be
  // correct immediately, before its own slide-in (x) animation runs. Every
  // other item already in the stack animates to its new spot.
  #reposition(instantEl = null) {
    let cursor = GAP;
    for (const item of this.#stack) {
      if (item.el === instantEl) {
        gsap.set(item.el, { bottom: cursor, right: GAP });
      } else {
        gsap.to(item.el, { bottom: cursor, duration: 0.4, ease: "power2.out", overwrite: "auto" });
      }
      cursor += item.el.offsetHeight + GAP;
    }
  }
}

export default new NotifStack();
